import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema} from '/vercel/path0/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/markdoc/tags.js'
import * as nodes from '/vercel/path0/markdoc/nodes.js'
const functions = {};
const schema = {
tags: tags ? (tags.default || tags) : {},
nodes: nodes ? (nodes.default || nodes) : {},
functions: functions ? (functions.default || functions) : {},
...(config ? (config.default || config) : {}),
};

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Integrate with Docusaurus\ndescription: Using Mendable AI in any Docusaurus website.\n---\n\nBefore install, make sure you have either signed up through our [platform](https://mendable.ai) or we have provided you with the `ANON_KEY`. Without the key, you won't be able to setup the search component.\n\nThis guide will show you how you can implement Mendable Docusaurus AI in your website.\n---\n\n\n\n## Installation\n\n### Installing Mendable Search\n\n```bash\nnpm install @mendable/search\n```\n\n## Configuration\n\nIn the `docusaurus.config.js` file, add the following:\n\n```js\ncustomFields:{\n    mendableAnonKey: \"YOUR_ANON_KEY\",\n},\n```\n\n### Install and run swizzle command\n\n```bash\nnpm i swizzle\n```\n\n1. Select the theme you'd like to swizzle. Normally it is the first option it appears.\n\n2. If you'd like to implement the Search Bar Component, select the `SearchBar`, otherwise for the Floating Button, select the `Footer`.\n\n3. Once you run swizzle, you will be prompted to either `wrap` or `eject` the component. Select `eject` from the menu as it is a safer option.\n\n## Search Bar Component\n\nReplace or edit your `src/theme/SearchBar.js` with the code below. In this example we are using the [Mendable Search Bar](/components/search-bar).\n.\n\n```jsx\nimport React from 'react'\nimport { MendableSearchBar } from '@mendable/search'\nimport useDocusaurusContext from '@docusaurus/useDocusaurusContext'\n\nexport default function SearchBarWrapper() {\n  const {\n    siteConfig: { customFields },\n  } = useDocusaurusContext()\n  return (\n    <div className=\"mendable-search\">\n      <MendableSearchBar\n        anon_key={customFields.mendableAnonKey}\n        style={{ accentColor: '#179C54', darkMode: false }}\n        placeholder=\"Search...\"\n        dialogPlaceholder=\"How to deploy this application?\"\n      />\n    </div>\n  )\n}\n```\n\n## Floating Button Component\n\nReplace or edit your `src/theme/Footer.js` with the code below. In this example we are using the [Mendable Floating Button](/components/floating-button).\n\n```jsx\nimport React from 'react'\nimport Footer from '@theme-original/Footer'\nimport useDocusaurusContext from '@docusaurus/useDocusaurusContext'\nimport { MendableFloatingButton } from '@mendable/search'\n\nexport default function FooterWrapper(props) {\n  const {\n    siteConfig: { customFields },\n  } = useDocusaurusContext()\n\n  return (\n    <>\n      <MendableFloatingButton anon_key={customFields.mendableAnonKey} />\n      <Footer {...props} />\n    </>\n  )\n}\n```\n\n## Keyword Search\n\nWe also provide keyword search as seen on the [Langchain Documentation](https://docs.langchain.com/). This combines keyword with AI Search in one modal. When the user starts typing, it will almost instantly display results for the search query. It also allows the user to use AI search by pressing \"Ask Mendable AI\".\n\n![](/images/keyword-search.png)\n\nTo enable keyword search, you need to pass the `showSimpleSearch` prop to the component.\n\n```jsx\nimport { MendableSearchBar } from \"@mendable/search\"\n\n<MendableSearchBar anon_key='YOUR_ANON_KEY' showSimpleSearch />\n```\n\n## Additional customization (Optional)\n\nIn some Docusaurus websites, the UI might get too busy in when viewing in mobile, so we recommend you to reduce the width of the search bar in mobile view.\nIn the `src/css/custom.css` file, add the following:\n\n```css\n/* Reduce width on mobile for Mendable Search */\n@media (max-width: 767px) {\n  .mendable-search {\n    width: 200px;\n  }\n}\n\n@media (max-width: 500px) {\n  .mendable-search {\n    width: 150px;\n  }\n}\n\n@media (max-width: 380px) {\n  .mendable-search {\n    width: 140px;\n  }\n}\n```\n";
const filepath = "/integrations/docusaurus.md";
const ast = Markdoc.parse(source);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    partials[key] = Markdoc.parse(partials[key]);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
